import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  HardHat, ArrowLeft, Camera, Image as ImageIcon, X, MapPin, Loader2,
  Check, Send, Save, AlertTriangle, Search, ChevronRight, Clock,
} from 'lucide-react';

const CARD = 'bg-[#2A3240] border-white/10';

function ProjectPicker({ projects, value, onSelect }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const selected = projects.find(p => p.id === value);
  const filtered = projects.filter(p => {
    const s = `${p.project_ref} ${p.project_code} ${p.client_name} ${p.site_address}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }).slice(0, 40);

  return (
    <div className="relative" data-testid="bc-project-picker">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left rounded-lg bg-[#1E2530] border border-white/10 px-3 py-3 flex items-center gap-2"
        data-testid="bc-project-picker-trigger"
      >
        {selected ? (
          <span className="flex-1 min-w-0">
            <span className="font-mono font-bold text-[#F5A623] text-sm">{selected.project_ref}</span>
            <span className="block text-gray-400 text-xs truncate">{selected.client_name} · {selected.site_address || 'No address'}</span>
          </span>
        ) : (
          <span className="flex-1 text-gray-400 text-sm">Choose the project…</span>
        )}
        <Search className="w-4 h-4 text-gray-500" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-lg bg-[#1E2530] border border-white/15 shadow-xl max-h-72 overflow-y-auto">
          <div className="p-2 sticky top-0 bg-[#1E2530]">
            <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search ref, client or address"
              className="bg-[#2A3240] border-white/10 text-white text-sm h-9" data-testid="bc-project-search" />
          </div>
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-gray-500 text-sm">No projects found</p>
          ) : filtered.map(p => (
            <button key={p.id} type="button"
              onClick={() => { onSelect(p); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2.5 hover:bg-white/5 border-t border-white/5"
              data-testid={`bc-project-option-${p.id}`}>
              <span className="font-mono font-bold text-[#F5A623] text-xs">{p.project_ref}</span>
              <span className="block text-gray-300 text-xs truncate">{p.client_name} · {p.site_address || 'No address'}</span>
              {!p.bc_contact_id && <span className="text-[10px] text-amber-400/80">No BC contact assigned</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function BuildingControl() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preProjectId = searchParams.get('project_id');
  const returnPath = searchParams.get('return');
  const lockMode = !!preProjectId;

  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [view, setView] = useState('list'); // list | new | result
  const [captures, setCaptures] = useState([]);
  const [listFilter, setListFilter] = useState('all');
  const [listLoading, setListLoading] = useState(true);

  // form state
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [project, setProject] = useState(null);
  const [stage, setStage] = useState('');
  const [stageCustom, setStageCustom] = useState('');
  const [note, setNote] = useState('');
  const [files, setFiles] = useState([]);
  const [geo, setGeo] = useState({ lat: null, lng: null, unavailable: false, status: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);

  useEffect(() => {
    api.get('/bc/me').then(r => setMe(r.data)).catch(() => setMe(null)).finally(() => setMeLoading(false));
  }, [api]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams({ scope: 'mine' });
      if (listFilter !== 'all') params.set('kind', listFilter);
      const r = await api.get(`/bc/captures?${params.toString()}`);
      setCaptures(r.data || []);
    } catch { toast.error('Failed to load captures'); }
    finally { setListLoading(false); }
  }, [api, listFilter]);

  useEffect(() => { if (view === 'list') loadList(); }, [view, loadList]);

  const startNew = async () => {
    setProject(null); setStage(''); setStageCustom(''); setNote(''); setFiles([]);
    setResult(null); setGeo({ lat: null, lng: null, unavailable: false, status: 'idle' });
    setView('new');
    try {
      const [pRes, sRes] = await Promise.all([api.get('/bc/projects'), api.get('/bc/stages')]);
      setProjects(pRes.data || []);
      setStages(sRes.data?.stages || []);
    } catch { toast.error('Failed to load form data'); }
    requestLocation();
  };

  const requestLocation = () => {
    if (!navigator.geolocation) { setGeo(g => ({ ...g, unavailable: true, status: 'unsupported' })); return; }
    setGeo(g => ({ ...g, status: 'locating' }));
    navigator.geolocation.getCurrentPosition(
      pos => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude, unavailable: false, status: 'ok' }),
      () => setGeo({ lat: null, lng: null, unavailable: true, status: 'denied' }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    setFiles(prev => [...prev, ...incoming].slice(0, 12));
  };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  // Launched from the Site Diary with a fixed project: jump straight into the
  // capture form with that project locked.
  useEffect(() => {
    if (meLoading || !me?.bc_capture_enabled || !preProjectId) return undefined;
    let active = true;
    (async () => {
      try {
        const [pRes, sRes] = await Promise.all([api.get('/bc/projects'), api.get('/bc/stages')]);
        if (!active) return;
        setProjects(pRes.data || []);
        setStages(sRes.data?.stages || []);
        const p = (pRes.data || []).find(x => x.id === preProjectId);
        if (p) setProject(p);
        setView('new');
        requestLocation();
      } catch { toast.error('Failed to load form data'); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meLoading, me, preProjectId]);

  const stageFinal = stage === 'Other' ? stageCustom.trim() : stage;
  const canSubmit = project && stageFinal && files.length > 0 && !submitting;
  const canSend = me?.can_send && project?.bc_contact_id;

  const submit = async (action) => {
    if (!canSubmit) return;
    if (action === 'send' && !project?.bc_contact_id) {
      toast.error('This project has no Building Control contact — an admin must assign one first.');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      fd.append('project_id', project.id);
      fd.append('stage', stage);
      if (stage === 'Other') fd.append('stage_custom', stageCustom.trim());
      if (note.trim()) fd.append('note', note.trim());
      fd.append('action', action);
      fd.append('location_unavailable', geo.unavailable ? 'true' : 'false');
      if (!geo.unavailable && geo.lat != null) { fd.append('lat', geo.lat); fd.append('lng', geo.lng); }
      const r = await api.post('/bc/captures', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult({ ...r.data, action });
      setView('result');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Capture failed');
    } finally { setSubmitting(false); }
  };

  if (meLoading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>;

  if (!me?.bc_capture_enabled) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <Card className={`${CARD} p-8 text-center max-w-md`}>
          <HardHat className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-white font-medium">Building Control not enabled</p>
          <p className="text-gray-400 text-sm mt-2">Ask an admin to switch on Building Control capture for your account.</p>
        </Card>
      </div>
    );
  }

  // ---------- RESULT ----------
  if (view === 'result' && result) {
    const fc = result.folder_copy || {};
    const sent = result.action === 'send';
    return (
      <div className="p-4 pb-28 max-w-lg mx-auto" data-testid="bc-result">
        <div className="flex flex-col items-center text-center gap-4 py-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${sent ? 'bg-amber-500/20' : 'bg-green-500/20'}`}>
            {sent ? <Send className="w-8 h-8 text-amber-400" /> : <Check className="w-8 h-8 text-green-400" />}
          </div>
          <div>
            <p className="text-white text-xl font-bold">{sent ? 'Saved & queued for Building Control' : 'Saved'}</p>
            <p className="text-gray-400 text-sm mt-1">{result.photos?.length || 0} photo{(result.photos?.length || 0) !== 1 ? 's' : ''} stamped · {result.stage}</p>
          </div>
          <Card className={`${CARD} w-full text-left`}>
            <CardContent className="p-4 space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-gray-500 w-20 shrink-0">Filing</span>
                <span className="text-gray-200">{fc.status === 'pending_preview' ? 'Pending (not run in preview — files on the live app)' : fc.status === 'done' ? 'Filed to SharePoint' : fc.status === 'blocked' ? 'No project folder yet' : 'Failed — retry from the record'}</span>
              </div>
              {sent && (
                <div className="flex items-start gap-2">
                  <span className="text-gray-500 w-20 shrink-0">Email</span>
                  <span className="text-gray-200">{result.delivery === 'queued' ? 'Queued (email not live yet)' : result.delivery === 'sent' ? `Sent to ${result.recipient_email}` : 'Failed'}</span>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex gap-3 mt-2">
            <Button onClick={() => { if (lockMode) { setResult(null); setStage(''); setStageCustom(''); setNote(''); setFiles([]); setGeo({ lat: null, lng: null, unavailable: false, status: 'idle' }); setView('new'); requestLocation(); } else startNew(); }} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="bc-capture-another"><Camera className="w-4 h-4 mr-2" /> Capture another</Button>
            <Button onClick={() => returnPath ? navigate(returnPath) : setView('list')} variant="outline" className="border-white/20 text-gray-300" data-testid="bc-done">Done</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- NEW ----------
  if (view === 'new') {
    return (
      <div className="p-4 pb-28 max-w-lg mx-auto space-y-4" data-testid="bc-new">
        <button onClick={() => returnPath ? navigate(returnPath) : setView('list')} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm" data-testid="bc-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><HardHat className="w-5 h-5 text-[#F5A623]" /> New Building Control capture</h1>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Project</label>
          {lockMode && project ? (
            <div className="rounded-lg bg-[#1E2530] border border-white/10 px-3 py-3" data-testid="bc-locked-project">
              <span className="font-mono font-bold text-[#F5A623] text-sm">{project.project_ref}</span>
              <span className="block text-gray-400 text-xs truncate">{project.client_name} · {project.site_address || 'No address'}</span>
            </div>
          ) : (
            <ProjectPicker projects={projects} value={project?.id} onSelect={setProject} />
          )}
          {project && !project.bc_contact_id && (
            <p className="text-[11px] text-amber-400/80 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> No BC contact — you can Save, but sending needs an admin to assign one.</p>
          )}
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Inspection stage</label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="bg-[#1E2530] border-white/10 text-white" data-testid="bc-stage-select"><SelectValue placeholder="Choose a stage" /></SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/10 max-h-72">
              {stages.map(s => <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {stage === 'Other' && (
            <Input value={stageCustom} onChange={e => setStageCustom(e.target.value)} placeholder="Describe the stage"
              className="mt-2 bg-[#1E2530] border-white/10 text-white" data-testid="bc-stage-custom" />
          )}
        </div>

        <div>
          <label className="text-xs text-gray-400 mb-1 block">Note (optional)</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Anything Building Control should know"
            className="w-full h-16 rounded-lg bg-[#1E2530] border border-white/10 text-white text-sm p-3 resize-none focus:outline-none focus:border-[#F5A623]/50" data-testid="bc-note" />
        </div>

        {/* GPS */}
        <Card className={CARD}>
          <CardContent className="p-3 flex items-center gap-2 text-sm">
            <MapPin className={`w-4 h-4 ${geo.status === 'ok' ? 'text-green-400' : 'text-gray-500'}`} />
            {geo.status === 'locating' ? <span className="text-gray-400">Getting location…</span>
              : geo.status === 'ok' ? <span className="text-gray-200" data-testid="bc-geo-ok">{geo.lat.toFixed(5)}, {geo.lng.toFixed(5)}</span>
              : <span className="text-amber-400/90" data-testid="bc-geo-unavailable">Location unavailable — photos will be stamped without GPS</span>}
            {geo.status !== 'ok' && geo.status !== 'locating' && (
              <button onClick={requestLocation} className="ml-auto text-[#F5A623] text-xs hover:underline" data-testid="bc-geo-retry">Try again</button>
            )}
          </CardContent>
        </Card>

        {/* Photos */}
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Photos</label>
          <div className="flex gap-2">
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => addFiles(e.target.files)} data-testid="bc-camera-input" />
            <input ref={libraryRef} type="file" accept="image/*" multiple className="hidden" onChange={e => addFiles(e.target.files)} data-testid="bc-library-input" />
            <Button type="button" onClick={() => cameraRef.current?.click()} className="flex-1 bg-[#1E2530] border border-white/10 text-white hover:bg-white/5" data-testid="bc-take-photo"><Camera className="w-4 h-4 mr-2" /> Take photo</Button>
            <Button type="button" onClick={() => libraryRef.current?.click()} variant="outline" className="flex-1 border-white/20 text-gray-300" data-testid="bc-choose-library"><ImageIcon className="w-4 h-4 mr-2" /> Library</Button>
          </div>
          {files.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3" data-testid="bc-photo-grid">
              {files.map((f, i) => (
                <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => removeFile(i)} className="absolute top-1 right-1 bg-black/60 rounded-full p-0.5" data-testid={`bc-remove-photo-${i}`}><X className="w-3 h-3 text-white" /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-[76px] pt-2 bg-gradient-to-t from-[#1E2530] via-[#1E2530] to-transparent space-y-2">
          <Button onClick={() => submit('save')} disabled={!canSubmit} className="w-full py-5 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-bold rounded-xl disabled:opacity-40" data-testid="bc-save-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
          {me?.can_send && (
            <Button onClick={() => submit('send')} disabled={!canSubmit || !canSend} className="w-full py-5 bg-emerald-600 text-white hover:bg-emerald-500 font-bold rounded-xl disabled:opacity-40" data-testid="bc-send-btn">
              <Send className="w-4 h-4 mr-2" /> {canSend ? 'Save & send to Building Control' : 'Send (assign a BC contact first)'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ---------- LIST ----------
  return (
    <div className="p-4 pb-28 max-w-2xl mx-auto" data-testid="bc-list-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><HardHat className="w-5 h-5 text-[#F5A623]" /> Building Control</h1>
          <p className="text-sm text-gray-400">Stamped inspection photos</p>
        </div>
        <Button onClick={startNew} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="bc-new-btn"><Camera className="w-4 h-4 mr-2" /> New</Button>
      </div>

      <div className="flex gap-2 mb-4">
        {['all', 'save', 'send'].map(f => (
          <button key={f} onClick={() => setListFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium ${listFilter === f ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#2A3240] text-gray-400 border border-white/10'}`}
            data-testid={`bc-filter-${f}`}>
            {f === 'all' ? 'All' : f === 'save' ? 'Saved' : 'Sent'}
          </button>
        ))}
      </div>

      {listLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" /></div>
      ) : captures.length === 0 ? (
        <Card className={CARD}><CardContent className="py-12 text-center">
          <HardHat className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No Building Control captures yet</p>
          <Button onClick={startNew} className="mt-3 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="bc-first-capture">Take your first capture</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {captures.map(c => (
            <Card key={c.id} className="bg-[#2A3240] border-white/5 hover:border-white/15 cursor-pointer transition-colors"
              onClick={() => navigate(`/portal/building-control/${c.id}`)} data-testid={`bc-card-${c.id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-[#1E2530] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <HardHat className="w-6 h-6 text-[#F5A623]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{c.project_ref}</p>
                  <p className="text-gray-400 text-xs truncate">{c.stage} · {c.photo_count} photo{c.photo_count !== 1 ? 's' : ''}</p>
                  <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />{(c.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge className={`text-[10px] ${c.kind === 'send' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-gray-300'}`}>{c.kind === 'send' ? 'Sent' : 'Saved'}</Badge>
                  {c.in_error && <Badge className="text-[10px] bg-red-500/20 text-red-300">In error</Badge>}
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default BuildingControl;
